import { useEffect, useRef, useCallback } from 'react';

/**
 * Toca um beep de alerta via Web Audio API sempre que `isOffline` muda para
 * true. Não toca na montagem inicial. Repete a cada `repeatInterval` ms
 * enquanto permanecer offline (padrão: 60 s). Use repeatInterval={0} para
 * tocar só uma vez.
 */
const useAlertaOffline = (isOffline, repeatInterval = 60000) => {
  const prevRef     = useRef(null);
  const timerRef    = useRef(null);
  const audioCtxRef = useRef(null);

  const playBeep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;

      const doPlay = () => {
        const now = ctx.currentTime;
        const beeps = [
          { freq: 880, start: 0,    dur: 0.18 },
          { freq: 660, start: 0.22, dur: 0.18 },
          { freq: 880, start: 0.44, dur: 0.18 },
        ];
        beeps.forEach(({ freq, start, dur }) => {
          const osc  = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, now + start);
          gain.gain.setValueAtTime(0.35, now + start);
          gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
          osc.start(now + start);
          osc.stop(now  + start + dur + 0.05);
        });
      };

      // AudioContext pode estar suspended por política de autoplay
      if (ctx.state === 'suspended') {
        ctx.resume().then(doPlay).catch(e => console.warn('[useAlertaOffline] resume falhou:', e));
      } else {
        doPlay();
      }
    } catch (e) {
      console.warn('[useAlertaOffline] Erro ao tocar beep:', e);
    }
  }, []);

  const stopRepeat = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRepeat = useCallback(() => {
    if (!repeatInterval) return;
    stopRepeat();
    timerRef.current = setInterval(playBeep, repeatInterval);
  }, [playBeep, stopRepeat, repeatInterval]);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = isOffline;

    // Ficou offline (transição → true): toca e agenda repetição
    if (isOffline === true && prev !== true) {
      playBeep();
      startRepeat();
    }

    // Voltou online ou ainda verificando: cancela repetição
    if (isOffline !== true) {
      stopRepeat();
    }

    return stopRepeat;
  }, [isOffline, playBeep, startRepeat, stopRepeat]);
};

export default useAlertaOffline;
